import React, { useState, useEffect, useMemo } from "react";
import "../assets/stylesheet/layout.scss";
import "../assets/stylesheet/dashboard.scss";
import "font-awesome/css/font-awesome.min.css";
import "../assets/stylesheet/engagement.scss";
import ReactPaginate from "react-paginate";
import { Bar } from "react-chartjs-2";
import "../assets/stylesheet/multiSelectInputType.css";

import {
  Chart as ChartJS,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  BarElement,
  LinearScale,
} from "chart.js";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import ApiRequest from "../services/apiService";
import baseUrl from "../services/baseUrl";
import GetFormattedDateTime from "../utilities/getFormattedDateTime";
import numeral from "numeral";
import ScreenLoader from "../components/screenLoader";

// ===== register chart==============
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// sample data
const sampleData = [
  {
    id: 1,
    name: "Avinash",
    RequestsReceived: 28,
    RequestsDelivered: 10,
    ResponseReceived: 15,
    ResponseRate: 15,
  },
  {
    id: 2,
    name: "Amit",
    RequestsReceived: 28,
    RequestsDelivered: 100,
    ResponseReceived: 15,
    ResponseRate: 15,
  },
  {
    id: 3,
    name: "Manav",
    RequestsReceived: 38,
    RequestsDelivered: 10,
    ResponseReceived: 35,
    ResponseRate: 25,
  },
  {
    id: 4,
    name: "Dummy",
    RequestsReceived: 58,
    RequestsDelivered: 40,
    ResponseReceived: 15,
    ResponseRate: 15,
  },
];

function Engagement() {
  const location = useLocation();
  const query = queryString.parse(location.search);

  const [engagementTrendsData, setEngagementTrendsData] = useState([]);
  const [engagementSummaryData, setEngagementSummaryData] = useState({});
  const [IsLoading, setIsLoading] = useState(false);

  const projectFilter = useMemo(() => {
    return query.pf ? JSON.parse(decodeURIComponent(query.pf)) : {};
  }, [query.pf]);

  const quickFilter = useMemo(() => {
    return query.qf ? JSON.parse(decodeURIComponent(query.qf)) : {};
  }, [query.qf]);

  const fromDate = useMemo(() => {
    return query.sd ? JSON.parse(decodeURIComponent(query.sd)) : {};
  }, [query.sd]);

  const toDate = useMemo(() => {
    return query.ed ? JSON.parse(decodeURIComponent(query.ed)) : {};
  }, [query.ed]);

  const fetchData = async (pf, qf, fd, ed) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        projectFilter: pf,
        quickFilterRange: qf,
        fromDate: fd,
        toDate: ed,
        queryMode: "4",
      });
      const url = `${baseUrl()}/nps/GetNpsDashboardChartsData?${params.toString()}`;
      const response = await ApiRequest({ apiLink: url, method: "GET" });

      if (response.Message === "Success") {
        setIsLoading(false);
        setEngagementSummaryData(response.Data.EngagementSummary);
        setEngagementTrendsData(response.Data.EngagementTrendsCharts);
      } else {
        console.log("Failed to load data");
      }
    } catch (error) {
      console.error("Error fetching cloud data:", error);
    }
  };
  useEffect(() => {
    fetchData(projectFilter, quickFilter, fromDate, toDate);
  }, [projectFilter, quickFilter, fromDate, toDate]);

  const [isScrolled, setIsScrolled] = useState(false);
  const handleScroll = () => {
    if (window.scrollY > 50) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const labels = engagementTrendsData.map((item) =>
    GetFormattedDateTime(item.RequestDate)
  );
  const requestsReceivedData = engagementTrendsData.map(
    (item) => item.RequestsReceived
  );
  const requestsDeliveredData = engagementTrendsData.map(
    (item) => item.RequestsDelivered
  );
  const responsesData = engagementTrendsData.map((item) => item.Responses);
  const responseRateData = engagementTrendsData.map((item) =>
    parseInt(item.ResponseRate)
  );

  const data = {
    labels: labels, // X-axis labels
    datasets: [
      {
        label: "Response Rate (%)",
        type: "line",
        data: responseRateData,
        fill: false,
        borderColor: "#333333",
        backgroundColor: "#333333",
        tension: 0.1,
        borderWidth: 2,
        yAxisID: "y-axis-2",
      },
      {
        label: "Requests",
        data: requestsReceivedData, // Data for the first group
        backgroundColor: "#234b98", // Bar color for Dataset 1
        borderWidth: 0,
      },
      {
        label: "People Contacted",
        data: requestsDeliveredData, // Data for the second group
        backgroundColor: "#E85C0D", // Bar color for Dataset 2
        borderWidth: 0,
      },
      {
        label: "Response",
        data: responsesData, // Data for the second group
        backgroundColor: "#86aed6", // Bar color for Dataset 2
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false, // Allows the canvas to adjust its size

    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: true, // Show or hide the legend
        position: "bottom", // Position of the legend
        align: "center", // Alignment of the legend
        labels: {
          padding: (0, 15, 0, 10), // Padding around the legend labels
          boxWidth: 7, // Width of the color box in the legend
          boxHeight: 7, // Height of the color box in the legend
          usePointStyle: true, // Use point styles instead of boxes
        },
        title: {
          display: true,
          padding: -12,
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return tooltipItem.dataset.label + ": " + tooltipItem.raw;
          },
        },
      },
    },
    layout: {
      padding: {
        bottom: 0, // Remove extra space below the chart
      },
    },
    scales: {
      x: {
        padding: 0,
        grid: {
          display: false,
        },
        stacked: false,
        title: {
          display: false,
          text: "Month",
        },
        ticks: {
          beginAtZero: true,
          padding: 10,
          display: true, // Hides the y-axis labels
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          suggestedMax: 50,
          suggestedMin: 1,
          beginAtZero: true,
          max: 100,
          autoSkip: true,
          lineHeight: 2,
        },
        stacked: false,
        title: {
          display: true,
          text: "Count",
        },
      },
      "y-axis-2": {
        grid: {
          display: false,
        },
        // min: 0,
        // max: 105,
        type: "linear",
        position: "right",
        title: {
          display: true,
          text: "Respnse Rate (%)",
        },
        beginAtZero: true,
        stacked: false, // Do not stack line chart
      },
     //--- min: 0,
    },
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [isSummaryView, setIsSummaryView] = useState(true);
  const [isDetailsView, setIsDetailsView] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "ascending",
  });

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0); // Reset to the first page on search
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handlePageSizeChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(0); // Reset to the first page on page size change
  };

  const sortedData = [...sampleData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? 1 : -1;
    }
    return 0;
  });

  const filteredData = sortedData.filter((item) => {
    // Check if the search term is in any column
    const searchLower = searchTerm.toLowerCase();
    return Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchLower)
    );
  });

  const paginatedData = filteredData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );
  //author-saksham
  //function-to toggle between the engagement detail/summary tab
  const showSummaryTab = () => {
    setIsDetailsView(false);
    setIsSummaryView(true);
  };

  const showDetailsTab = () => {
    setIsSummaryView(false);
    setIsDetailsView(true);
  };
  //end

  return (
    <>
      {IsLoading ? (
        <div className="screenLoader">
          <ScreenLoader />
        </div>
      ) : (
        <section className="container-fluid">
          {/* filter area */}
          {/* top filter area */}
          <div className="row mb-2">
            <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
              <div className="card">
                <div className="card-option ms-auto">
                  {/* <ButtonGroup className='view-type' size='sm'>
                            <Button variant="outline-secondary" className='active px-2 py-1'><i className='fa fa-pie-chart' aria-hidden="true"></i> </Button>
                            <Button variant="outline-secondary" className='px-2 py-1'><i className='fa fa-table' aria-hidden="true"></i></Button>
                        </ButtonGroup> */}
                  <a href={"/home"} className="btn btn-primary">
                    <i class="fa fa-chevron-left" aria-hidden="true"></i> Back
                  </a>
                </div>
              </div>
            </div>
          </div>
          {/* section area */}
          <div className="row">
            {isSummaryView ? (
              <>
                {/* summary container */}
                <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mb-2">
                  <div className="row g-2">
                    {/* engagement summary */}
                    <div className="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-4">
                      <div className="card" style={{ height: "100%" }}>
                        <div className="card-body p-0">
                          <div className="card-title d-flex align-items-center">
                            <h3>Engagement Summary</h3>
                          </div>
                          <div
                            className="card-content align-items-center d-flex p-3"
                            style={{ height: "calc(100% - 50px)" }}
                          >
                            <div className="row g-2">
                              <div className="col-6 text-center">
                                <div className="eng-summary eng-summary-1">
                                  <div>
                                    <div className="service-name">Requests</div>
                                    <div className="service-count">
                                      {engagementSummaryData.RequestsReceived}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-6 text-center">
                                <div className="eng-summary eng-summary-2">
                                  <div>
                                    <div className="service-name">
                                      People Contacted
                                    </div>
                                    <div className="service-count">
                                      {engagementSummaryData.RequestsDelivered}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-6 text-center">
                                <div className="eng-summary eng-summary-3">
                                  <div>
                                    <div className="service-name">
                                      Responses
                                    </div>
                                    <div className="service-count">
                                      {engagementSummaryData.Responses}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-6 text-center">
                                <div className="eng-summary eng-summary-4">
                                  <div>
                                    <div className="service-name">
                                      Response Rate %
                                    </div>
                                    <div className="service-count">
                                      <span>
                                        {numeral(
                                          engagementSummaryData.ResponseRate
                                        ).format("0.00")}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* engagement trends */}
                    <div className="col-12 col-sm-12 col-md-6 col-lg-8 col-xl-8">
                      <div className="card">
                        <div className="card-body p-0">
                          <div className="card-title d-flex align-items-center">
                            <h3>Engagement Trends</h3>
                          </div>
                          <div className="card-content p-2 pt-1 pb-0">
                            <div style={{ height: "300px" }}>
                              <Bar data={data} options={options} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* details contailner */}
                <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mb-2">
                  <div className="card">
                    <div className="card-body pt-2 pb-2">
                      <div className="card-content">
                        <div className="row align-items-center g-2">
                          <div className="col-12 col-sm-6 col-md-6">
                            <div className="d-flex align-items-center">
                              <label htmlFor="page-size">Items per page:</label>
                              <select
                                className="form-control-sm h-30 me-3"
                                id="page-size"
                                value={itemsPerPage}
                                onChange={handlePageSizeChange}
                              >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={15}>15</option>
                                <option value={20}>20</option>
                              </select>
                              <input
                                className="form-control-sm did-floating-input form-control h-30 w-200px"
                                type="text"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                placeholder="Type here to search ..."
                              />
                            </div>
                          </div>

                          <div className="col-12 col-sm-12 col-md-6">
                            <p className="mb-1 me-1 paginationTotalCount">
                              Total items: {filteredData.length}
                            </p>
                            <ReactPaginate
                              previousLabel={"Previous"}
                              nextLabel={"Next"}
                              breakLabel={"..."}
                              pageCount={Math.ceil(
                                filteredData.length / itemsPerPage
                              )}
                              marginPagesDisplayed={2}
                              pageRangeDisplayed={5}
                              onPageChange={handlePageClick}
                              containerClassName={"pagination"}
                              activeClassName={"active"}
                            />
                          </div>

                          <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
                            <div className="table-responsive custom-table">
                              <table className="table table-sm  table-hover">
                                <thead>
                                  <tr>
                                    <th onClick={() => handleSort("name")}>
                                      Name{" "}
                                      {sortConfig.key === "name"
                                        ? sortConfig.direction === "ascending"
                                          ? "↑"
                                          : "↓"
                                        : ""}
                                    </th>
                                    <th
                                      className="text-end"
                                      onClick={() =>
                                        handleSort("RequestsReceived")
                                      }
                                    >
                                      Requests Received{" "}
                                      {sortConfig.key === "RequestsReceived"
                                        ? sortConfig.direction === "ascending"
                                          ? "↑"
                                          : "↓"
                                        : ""}
                                    </th>
                                    <th
                                      className="text-end"
                                      onClick={() =>
                                        handleSort("RequestsDelivered")
                                      }
                                    >
                                      Requests Delivered{" "}
                                      {sortConfig.key === "RequestsDelivered"
                                        ? sortConfig.direction === "ascending"
                                          ? "↑"
                                          : "↓"
                                        : ""}
                                    </th>
                                    <th
                                      className="text-end"
                                      onClick={() =>
                                        handleSort("ResponseReceived")
                                      }
                                    >
                                      Response Received{" "}
                                      {sortConfig.key === "ResponseReceived"
                                        ? sortConfig.direction === "ascending"
                                          ? "↑"
                                          : "↓"
                                        : ""}
                                    </th>
                                    <th
                                      className="text-end"
                                      onClick={() => handleSort("ResponseRate")}
                                    >
                                      Response Rate (%){" "}
                                      {sortConfig.key === "ResponseRate"
                                        ? sortConfig.direction === "ascending"
                                          ? "↑"
                                          : "↓"
                                        : ""}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paginatedData.map((item) => (
                                    <tr key={item.id}>
                                      <td>{item.name}</td>
                                      <td className="text-end">
                                        {item.RequestsReceived}
                                      </td>
                                      <td className="text-end">
                                        {item.RequestsDelivered}
                                      </td>
                                      <td className="text-end">
                                        {item.ResponseReceived}
                                      </td>
                                      <td className="text-end">
                                        {item.ResponseRate}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      )}
    </>
  );
}

export default Engagement;
